import { useState, useEffect } from 'react';

type ViewMode = 'compact' | 'full';

type PanelType = 'nextMatch' | 'lastResult' | 'teamStatus' | 'teamOverview' | 'weeklyPundit';

const STORAGE_KEY = 'panel-view-preferences';

const getDefaultMode = (): ViewMode => {
    if (typeof window === 'undefined') return 'full';
    const isMobile = window.innerWidth < 768;
    return isMobile ? 'compact' : 'full';
};

const loadPreferences = (): Record<PanelType, ViewMode> => {
    if (typeof window === 'undefined') {
        return {
            nextMatch: 'full',
            lastResult: 'full',
            teamStatus: 'full',
            teamOverview: 'full',
            weeklyPundit: 'full'
        };
    }

    const defaultMode = getDefaultMode();
    const stored = localStorage.getItem(STORAGE_KEY);

    if (!stored) {
        return {
            nextMatch: defaultMode,
            lastResult: defaultMode,
            teamStatus: defaultMode,
            teamOverview: defaultMode,
            weeklyPundit: defaultMode
        };
    }

    try {
        return JSON.parse(stored);
    } catch {
        return {
            nextMatch: defaultMode,
            lastResult: defaultMode,
            teamStatus: defaultMode,
            teamOverview: defaultMode,
            weeklyPundit: defaultMode
        };
    }
};

const savePreferences = (preferences: Record<PanelType, ViewMode>) => {
    if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    }
};

export const usePanelViewPreferences = () => {
    const [preferences, setPreferences] = useState<Record<PanelType, ViewMode>>(loadPreferences);

    const toggleView = (panel: PanelType) => {
        setPreferences(prev => {
            const updated = {
                ...prev,
                [panel]: prev[panel] === 'compact' ? 'full' : 'compact'
            };
            savePreferences(updated);
            return updated;
        });
    };

    const getViewMode = (panel: PanelType): ViewMode => {
        return preferences[panel];
    };

    const isCompact = (panel: PanelType): boolean => {
        return getViewMode(panel) === 'compact';
    };

    return {
        toggleView,
        getViewMode,
        isCompact,
        preferences
    };
};
